import { math } from './mathjs-config';

// --- Type Definitions ---
export type Kind = 'volume' | 'weight' | 'count' | 'seasoning';
export type Unit = string;

// --- Constants ---
const SEASONING_KEYWORDS = [
  'salt', 'pepper', 'paprika', 'cumin', 'coriander', 'chili', 'chilli', 'garlic', 'onion powder', 'ginger',
  'oregano', 'basil', 'thyme', 'parsley', 'cilantro', 'cinnamon', 'nutmeg', 'clove', 'cloves'
];

const UNIT_ALIASES: Record<string, string> = {
  // volume
  teaspoon: 'tsp', teaspoons: 'tsp', tsp: 'tsp', tsps: 'tsp',
  tablespoon: 'tbsp', tablespoons: 'tbsp', tbsp: 'tbsp', tbsps: 'tbsp',
  cup: 'cup', cups: 'cup',
  ml: 'ml', milliliter: 'ml', milliliters: 'ml',
  l: 'l', liter: 'l', liters: 'l', litre: 'l', litres: 'l',
  // weight
  g: 'g', gram: 'g', grams: 'g',
  kg: 'kg', kilogram: 'kg', kilograms: 'kg',
  oz: 'oz', ounce: 'oz', ounces: 'oz',
  lb: 'lb', lbs: 'lb', pound: 'lb', pounds: 'lb',
  // count-ish
  piece: '', pieces: '', unit: '', units: '', item: '', items: '',
  strip: '', strips: '', clove: '', cloves: '', bunch: '', head: '', bulb: '',
  // keep bare '' as ''
  '': ''
}

export function normalizeUnit(unit?: string | null): string {
  const u = String(unit || '').trim().toLowerCase()
  return (UNIT_ALIASES[u] ?? u) as string
}

export function escalateUnit(unit: string, qty: number): { unit: string; qty: number } {
  let u = normalizeUnit(unit)
  let q = qty
  if (u === 'tsp' && q >= 3) { q = q / 3; u = 'tbsp' }
  if (u === 'tbsp' && q >= 16) { q = q / 16; u = 'cup' }
  if (u === 'g' && q >= 1000) { q = q / 1000; u = 'kg' }
  if (u === 'oz' && q >= 16) { q = q / 16; u = 'lb' }
  return { unit: u, qty: q }
}

export function unitKind(unit: Unit): Kind {
  try {
    const mjsUnit = math.unit(1, unit as string);
    if (mjsUnit.hasBase('L')) return 'volume';
    if (mjsUnit.hasBase('g')) return 'weight';
    return 'count';
  } catch (e) {
    return 'count';
  }
}

export function isSeasoning(name?: string): boolean {
  const n = String(name || '').toLowerCase();
  return SEASONING_KEYWORDS.some(k => n.includes(k));
}

export function inferKind(unit: Unit, name?: string): Kind {
  if (isSeasoning(name)) return 'seasoning';
  return unitKind(unit);
}

export function scaleQuantitySmart(quantity: number, unit: Unit, factor: number, kind: Kind): { qty: number; unit: string } {
  const scale = kind === 'seasoning' ? Number(math.sqrt(factor)) : factor;
  const scaledValue = Number(math.multiply(quantity, scale));

  const { qty, unit: newUnit } = escalateUnit(unit, scaledValue);

  return { qty, unit: newUnit };
}

// --- Formatting Functions ---

export function roundCulinary(value: number, unit: string): number {
    // This can be simplified or made more robust with mathjs in the future,
    // but for now, we can keep the existing logic.
    const u = unit; // Assuming unit is already normalized by mathjs
    if (u === 'tsp' || u === 'tbsp') return roundToDenominator(value, 8);
    if (u === 'cup') return roundToDenominator(value, 6);
    if (u === 'oz' || u === 'lb') return Math.round(value * 100) / 100;
    if (u === 'g' || u === 'kg') return Math.round(value * 10) / 10;
    if (value < 1) return roundToDenominator(value, 4);
    return roundToDenominator(value, 2);
}

function roundToDenominator(value: number, denom: number): number {
  return Math.round(value * denom) / denom;
}

const FRACTIONS: Array<{ v: number; s: string }> = [
  { v: 0.875, s: '⅞' }, { v: 0.75, s: '¾' }, { v: 0.6667, s: '⅔' },
  { v: 0.625, s: '⅝' }, { v: 0.5, s: '½' }, { v: 0.3333, s: '⅓' },
  { v: 0.25, s: '¼' }, { v: 0.125, s: '⅛' },
];

export function toPrettyFraction(value: number): string {
  const whole = Math.trunc(value);
  const frac = Math.abs(value - whole);
  for (const f of FRACTIONS) {
    if (Math.abs(frac - f.v) < 0.06) {
      return whole ? `${whole}${f.s}` : f.s;
    }
  }
  if (Math.abs(value) < 1) return (Math.round(value * 100) / 100).toString();
  return (Math.round(value * 10) / 10).toString();
}

export function pluralize(unit: string, qty: number): string {
    if (!unit) return '';
    if (qty !== 1 && !unit.endsWith('s')) {
        // Basic pluralization, mathjs doesn't handle this.
        if (unit === 'cup') return 'cups';
        return `${unit}s`;
    }
    return unit;
}

export function formatQty(qty: number, unit: string): string {
  if (!qty) return '0';
  const pretty = toPrettyFraction(qty);
  const out = pluralize(unit, qty);
  return [pretty, out].filter(Boolean).join(' ').trim();
}

// --- Other Utility Functions (Kept as is) ---

export function normalizeIngredientDisplay(name?: string): { name: string; notesExtra?: string } {
  const raw = String(name || '').trim();
  if (!raw) return { name: '' };
  const lower = raw.toLowerCase();
  const splitters = [' crosswise', ' lengthwise', ' into ', ' sliced', ' chopped', ' minced'];
  for (const s of splitters) {
    const idx = lower.indexOf(s);
    if (idx > 0) {
      const base = raw.slice(0, idx).trim();
      const rest = raw.slice(idx).trim();
      return { name: base || raw, notesExtra: rest };
    }
  }
  return { name: raw };
}

export function canonicalizeIngredientName(name: string): string {
  // This logic is independent of mathjs
  let out = name;
  const NAME_SYNONYMS: Array<[RegExp, string]> = [
    [/\bbleu cheese\b/i, 'blue cheese'],
    [/\bpepper sauce\b/i, 'hot sauce'],
  ];
  for (const [re, rep] of NAME_SYNONYMS) {
    out = out.replace(re, rep);
  }
  return out;
}

export function formatRange(minQty: number, maxQty: number, unit: string): string {
    const u = unit;
    const left = formatQty(minQty, u);
    const right = formatQty(maxQty, u);
    const unitStr = pluralize(u, maxQty);
    const [lq] = left.split(' ');
    const [rq] = right.split(' ');
    return `${lq}–${rq} ${unitStr}`.trim();
}
